// The redesign renamed several addresses (Orgs → Customers, Billing → Invoices, Price book → Model
// prices, Expense → AWS costs) and folded the per-org billing screens into the customer page.
// Old bookmarks and links in guides keep working: main.tsx replaces a legacy address with its new
// one before resolving it. Pure, so the whole table is unit-tested.
import { matchPath, type RouterLocation } from "./router";

/** An org tab of the old SCR-004 → the customer tab that now holds its content. */
export const LEGACY_ORG_TABS: Readonly<Record<string, string>> = {
  overview: "usage",
  installations: "aws-accounts",
  credentials: "keys",
  oidc: "sign-in",
  aws: "sign-in",
  settings: "settings",
};

type Rule = [pattern: string, target: (params: Record<string, string>) => string];

const enc = encodeURIComponent;

const RULES: readonly Rule[] = [
  ["/orgs", () => "/customers"],
  ["/orgs/:orgId", (p) => `/customers/${enc(p["orgId"] ?? "")}/usage`],
  ["/orgs/:orgId/:tab", (p) => `/customers/${enc(p["orgId"] ?? "")}/${LEGACY_ORG_TABS[p["tab"] ?? ""] ?? "usage"}`],
  ["/billing", () => "/invoices"],
  ["/billing/:orgId/:period", (p) => `/customers/${enc(p["orgId"] ?? "")}/invoices/${enc(p["period"] ?? "")}`],
  [
    "/billing/:orgId/:period/adjustments",
    (p) => `/customers/${enc(p["orgId"] ?? "")}/invoices/${enc(p["period"] ?? "")}/adjustments`,
  ],
  ["/rate-plans/orgs/:orgId", (p) => `/customers/${enc(p["orgId"] ?? "")}/billing`],
  // NAV-038 (REQ-238): Users & teams became People, Prompt logs moved under Activity.
  ["/customers/:orgId/users", (p) => `/customers/${enc(p["orgId"] ?? "")}/people/users`],
  ["/customers/:orgId/prompt-logs", (p) => `/customers/${enc(p["orgId"] ?? "")}/activity/prompts`],
  ["/prices", () => "/model-prices"],
  ["/expense", () => "/aws-costs"],
  ["/expense/:orgId/:period", (p) => `/aws-costs/${enc(p["orgId"] ?? "")}/${enc(p["period"] ?? "")}`],
];

/** The new address for a legacy one (query string kept), or null when the address is current. */
export function legacyRedirect(location: RouterLocation): string | null {
  for (const [pattern, target] of RULES) {
    const params = matchPath(pattern, location.pathname);
    if (params !== null) return target(params) + location.search;
  }
  return null;
}
